import { useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  makeStyles,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { useSnackbar } from 'notistack';

import { ReactComponent as TagsIcon } from 'assets/images/tag-icon-v2.svg';
import SearchBar from 'components/SearchBar';
import { useAppDispatch, useAppSelector } from 'store';
import {
  addTagToFunnelOpportunity,
  funnelsSelector,
  FunnelTag,
  removeTagFromFunnelOpportunity,
} from 'store/reducers/funnels';
import { fetchTags } from 'store/reducers/tags';

import { TagItem } from './TagItem';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  paper: {
    width: 250,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    padding: spacing(1.5, 0, 0.5, 2),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    overflowX: 'hidden',
    minHeight: spacing(30),
    maxHeight: spacing(30),
    paddingRight: spacing(1),
  },
  triggerIcon: {
    width: spacing(2),
    height: spacing(2),

    '& path': {
      fill: palette.text.primary,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: spacing(1),
  },
  chipLabel: {
    fontSize: typography.pxToRem(10),
    fontWeight: typography.fontWeightMedium as number,
    cursor: 'pointer',
    paddingLeft: spacing(1.5),
  },
}));

interface OpportunityTagsProps {
  tags: FunnelTag[];
  disabled?: boolean;
  readOnly?: boolean;
  inline?: boolean;
  funnelId: string;
  stageId: string;
  opportunityId: string;
  onSuccess?: (tag: FunnelTag) => void;
}

export default function OpportunityTags({
  tags,
  disabled = false,
  readOnly = false,
  inline = false,
  onSuccess,
  funnelId,
  stageId,
  opportunityId,
}: OpportunityTagsProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { funnelTags: tagsList, loadingFunnelTags: isLoading } =
    useAppSelector(funnelsSelector);

  const [searchTerm, setSearchTerm] = useState('');
  const [tagAdd, setTagAdd] = useState<string>();
  const [localTags, setLocalTags] = useState<FunnelTag[]>(tags);

  // Sincroniza o estado local com as props quando elas mudam
  useEffect(() => {
    setLocalTags(tags);
  }, [tags]);

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const onResetSearch = () => {
    setSearchTerm('');
  };

  const handleAddTag = (tag: FunnelTag) => {
    const tagId = tag.id;

    dispatch(
      addTagToFunnelOpportunity({
        funnelId,
        stageId,
        opportunityId,
        tagId,
      }),
    )
      .unwrap()
      .then(({ data: tag }) => {
        onSuccess?.(tag);
      })
      .catch(error => {
        if (error.response) enqueueSnackbar(error.response.data.message);
      });
  };

  const handleRemoveTag = (tagId?: string) => {
    if (!tagId || disabled || readOnly) return;

    dispatch(
      removeTagFromFunnelOpportunity({
        funnelId,
        stageId,
        opportunityId,
        tagId,
      }),
    )
      .unwrap()
      .then(({ data: tag }) => {
        onSuccess?.(tag);
      })
      .catch(error => {
        if (error.response) enqueueSnackbar(error.response.data.message);
      });
  };

  // Toggle tag (adiciona ou remove)
  const handleToggleTag = (tag: FunnelTag) => {
    if (readOnly || disabled) return;

    const isTagSelected = localTags.some(t => t.id === tag.id);

    if (isTagSelected) {
      // Atualiza estado local imediatamente (UI otimista)
      setLocalTags(prev => prev.filter(t => t.id !== tag.id));
      handleRemoveTag(tag.id);
    } else {
      // Atualiza estado local imediatamente (UI otimista)
      setLocalTags(prev => [
        ...prev,
        { ...tag, name: tag.name || '', color: tag.color || '' },
      ]);
      handleAddTag(tag);
    }
  };

  const renderTriggerButton = () => {
    if (readOnly) return null;

    return (
      <Tooltip arrow title="Tags da oportunidade">
        <IconButton aria-label="Tags" size="small" style={{ padding: 8 }}>
          <TagsIcon className={classes.triggerIcon} />
        </IconButton>
      </Tooltip>
    );
  };

  const renderInlineContent = () => {
    if (localTags.length > 0) {
      return localTags.map(tag => (
        <TagItem
          key={tag.id}
          tag={tag}
          tagAdd={tagAdd}
          onRemove={readOnly ? undefined : handleRemoveTag}
        />
      ));
    }
    return renderTriggerButton();
  };

  const renderReducedContent = () => {
    return (
      <Box display="flex" alignItems="center" gridGap={4}>
        {localTags.length > 0 && (
          <>
            <TagItem
              key={localTags[0].id}
              tag={localTags[0]}
              tagAdd={tagAdd}
              onRemove={readOnly ? undefined : handleRemoveTag}
            />

            {localTags.length > 1 && (
              <Chip
                style={{ cursor: 'pointer', margin: 4 }}
                label={`+${localTags.length - 1}`}
                size="small"
                variant="outlined"
              />
            )}
          </>
        )}

        {localTags.length < 2 && renderTriggerButton()}
      </Box>
    );
  };

  return (
    <PopupState variant="popover" popupId="popup-tags-v2">
      {popupState => (
        <Box onClick={e => e.stopPropagation()} width="fit-content">
          <Box
            display="flex"
            alignItems="center"
            gridGap={4}
            flexWrap="wrap"
            width="fit-content"
            {...(!readOnly ? bindTrigger(popupState) : {})}
          >
            {inline ? renderInlineContent() : renderReducedContent()}

            {inline && localTags.length > 0 && renderTriggerButton()}
          </Box>

          {!readOnly && (
            <Popover
              {...bindPopover(popupState)}
              TransitionProps={{ onEnter: () => dispatch(fetchTags()) }}
              classes={{ paper: classes.paper }}
              onClick={e => e.stopPropagation()}
              onMouseEnter={() => setTagAdd('')}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                gridGap={8}
                style={{ paddingRight: 16 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 600, paddingLeft: 8 }}
                  >
                    Tags
                  </Typography>
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={popupState.close}
                  >
                    <Close />
                  </IconButton>
                </Box>

                <SearchBar
                  value={searchTerm}
                  onChange={onSearchChange}
                  onCancelSearch={onResetSearch}
                  placeholder="Pesquisar"
                  debounceTimer={0}
                  showSearchIcon
                  size="small"
                />
              </Box>

              <List disablePadding classes={{ root: classes.list }}>
                {isLoading && (
                  <ListItem disableGutters style={{ justifyContent: 'center' }}>
                    <CircularProgress color="inherit" size={24} />
                  </ListItem>
                )}

                {!isLoading && tagsList.length === 0 && (
                  <ListItem disableGutters>
                    <Typography variant="body2">Sem tags</Typography>
                  </ListItem>
                )}

                {!isLoading &&
                  tagsList
                    .filter(tag => tag.name?.toLowerCase().includes(searchTerm))
                    .map(tag => (
                      <ListItem
                        disableGutters
                        dense
                        key={tag.id}
                        button
                        disabled={disabled}
                        onClick={() => handleToggleTag(tag)}
                      >
                        <Box className={classes.row}>
                          <TagItem tag={tag} />
                          <Checkbox
                            checked={localTags.some(t => t.id === tag.id)}
                            size="small"
                            color="primary"
                            disabled={disabled}
                          />
                        </Box>
                      </ListItem>
                    ))}
              </List>
            </Popover>
          )}
        </Box>
      )}
    </PopupState>
  );
}
